import { ConfirmModal } from "@/components";
import * as S from "@/components/DynamicForm/styled";
import { ViperUserSearchSelect } from "@/components/ViperSelect/ViperUserSearchSelect";
import { CustomWidthDiv } from "@/containers/Project/components/styled";
import { getFlattenedProjectLeadersFromForm } from "@/containers/Project/helper";
import { useUserContext } from "@/providers";
import { useGetFormLookupsQuery, useGetUsersByIdsMutation } from "@/services";
import { any, bool, func, object, shape, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

export const ProjectLeaderFields = ({
  regionalProjectLeadersProp,
  backupProjectLeaderProp,
  formRef,
  readOnly,
  fromProject,
  isSaving,
  onFinish,
  ...rest
}) => {
  const intl = useIntl();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [flattenedProjectLeaders, setFlattenedProjectLeaders] = useState([]);
  const [showConfirmDelete, toggleShowConfirmDelete] = useState(false);
  const { userToken } = useUserContext();
  const [previousFormValues, setPreviousFormValues] = useState(null);

  const [getUsersByUserIds, { data: getUsersByIds }] =
    useGetUsersByIdsMutation();

  useEffect(() => {
    setSelectedUsers(getUsersByIds);
  }, [getUsersByIds]);

  const [regions, setRegions] = useState(null);

  const { data: forms } = useGetFormLookupsQuery();

  useEffect(() => {
    setRegions(forms?.intake?.region);
  }, [forms?.intake]);

  useEffect(() => {
    if (!selectedUsers) return;

    validateProjectLeaders(formRef);

    const projectLeaders = Object.values(
      regionalProjectLeadersProp?.initialValues || {}
    );
    const users = [backupProjectLeaderProp?.initialValues, ...projectLeaders];
    const userIds = users.filter((id) => !!id);
    const unique = [...new Set(userIds)];
    const selectedUserIds = selectedUsers.map(({ id }) => !!unique[id]);

    if (unique.length && selectedUserIds.length !== unique.length) {
      getUsersByUserIds(unique);
    }
  }, [
    backupProjectLeaderProp?.initialValues,
    formRef,
    regionalProjectLeadersProp?.initialValues,
    selectedUsers,
    getUsersByUserIds,
  ]);

  const validateProjectLeaders = (form) => {
    const flattenedLeaders = getFlattenedProjectLeadersFromForm(form);
    setFlattenedProjectLeaders(flattenedLeaders);
  };

  const checkRegionPopulated = (regionId) =>
    regionId
      ? !!formRef?.getFieldValue("projectLeaders")?.[regionId]
      : !!formRef?.getFieldValue("backupProjectLeader");

  const handleUserSelectChange = (value) => {
    validateProjectLeaders(formRef);
    const formValues = formRef.getFieldsValue();
    const userId = userToken?.user?.id;
    const userIsProjectLeader =
      Object.values(formValues.projectLeaders).some((id) => id === userId) ||
      formValues.backupProjectLeader === userId;

    const userWasProjectLeader =
      Object.values(regionalProjectLeadersProp?.initialValues || {}).some(
        (id) => id === userId
      ) || backupProjectLeaderProp?.initialValues === userId;

    if (userId && userWasProjectLeader && !userIsProjectLeader) {
      toggleShowConfirmDelete(true);
    } else {
      if (onFinish) onFinish();

      setPreviousFormValues(formValues);
    }
  };

  const handleConfirmRemoveSelf = () => {
    if (onFinish) onFinish();
    toggleShowConfirmDelete(false);
  };

  const handleCancelChange = () => {
    toggleShowConfirmDelete(false);
    formRef.setFieldsValue(
      previousFormValues || {
        projectLeaders: regionalProjectLeadersProp.initialValues,
        backupProjectLeader: backupProjectLeaderProp.initialValues,
      }
    );
  };

  const getExistingProjectLeaderForRegion = (regionId) => {
    const leaders = formRef.getFieldValue("projectLeaders");

    if (!leaders) return null;

    if (!selectedUsers) return null;

    for (let i = 0; i < selectedUsers.length; i++) {
      if (selectedUsers[i].id === leaders[regionId]) {
        return selectedUsers[i];
      }
    }

    return null;
  };

  const getBackupProjectLeaderUser = () => {
    if (!selectedUsers) return null;

    const backupProjectLeader = formRef.getFieldValue("backupProjectLeader");

    for (let i = 0; i < selectedUsers.length; i++) {
      if (selectedUsers[i].id === backupProjectLeader) {
        return selectedUsers[i];
      }
    }
  };

  const renderCustomSelect = (label, name, initialValue, regionId, index) => {
    const formAlreadySetInitialValue = formRef.getFieldValue(name);
    const initialValueProp = formAlreadySetInitialValue
      ? {}
      : {
          initialValue,
        };

    let projectLeader = getExistingProjectLeaderForRegion(regionId);
    if (regionId == null) {
      projectLeader = getBackupProjectLeaderUser(regionId);
    }

    return (
      <S.CustomSelectFormItem
        $empty={!checkRegionPopulated(regionId)}
        rules={[
          {
            required: !flattenedProjectLeaders?.length > 0,
            message: intl.formatMessage({
              id: "regionalProjectLeaderCard.validationMessage",
            }),
          },
        ]}
        key={index}
        label={label}
        name={name}
        {...initialValueProp}
      >
        <ViperUserSearchSelect
          data-test-id={`viper-user-search-select-${regionId}`}
          id={regionId}
          name={name}
          onChange={handleUserSelectChange}
          formRef={formRef}
          disabled={isSaving || readOnly}
          readOnly={readOnly}
          allowClear={!fromProject || flattenedProjectLeaders?.length > 1}
          existingProjectLeader={projectLeader}
          {...rest}
        />
      </S.CustomSelectFormItem>
    );
  };

  return (
    <CustomWidthDiv $width={fromProject ? "100%" : "60%"}>
      <S.CustomFormAlert
        type={flattenedProjectLeaders?.length > 0 ? "warning" : "error"}
        message={intl.formatMessage({
          id: "regionalProjectLeaderCard.validationMessage",
        })}
      />
      {regions && formRef && (
        <>
          {Object.keys(regions).map((regionId, index) =>
            renderCustomSelect(
              regions[regionId].fallback,
              [regionalProjectLeadersProp.propertyName, regionId],
              regionalProjectLeadersProp?.initialValues?.[regionId],
              regionId,
              index
            )
          )}
          {renderCustomSelect(
            intl.formatMessage({
              id: "regionalProjectLeaderCard.backupProjectLeader",
            }),
            backupProjectLeaderProp.propertyName,
            backupProjectLeaderProp?.initialValues,
            null,
            null
          )}
        </>
      )}

      <ConfirmModal
        onSaveChanges={handleConfirmRemoveSelf}
        hidden={!showConfirmDelete}
        toggle={handleCancelChange}
        headerTitleKey={"regionalProjectLeaderCard.removeSelfTitle"}
        bodyTextKey={"regionalProjectLeaderCard.removeSelfDescription"}
        cancelLabel={intl.formatMessage({ id: "general.cancel" })}
      />
    </CustomWidthDiv>
  );
};

ProjectLeaderFields.propTypes = {
  regionalProjectLeadersProp: shape({
    propertyName: string,
    initialValues: object,
  }).isRequired,
  backupProjectLeaderProp: shape({
    propertyName: string,
    initialValues: string,
  }).isRequired,
  formRef: any,
  readOnly: bool,
  fromProject: bool,
  isSaving: bool,
  onFinish: func,
};
